import { StatusCode } from "api/protocols";
import {
  GetBookingsByCategory,
  GetEvents,
  GetTeeTimesPlayerVolume,
} from "api/rpc/2024-04/facilityAdmin/dashboard/dashboard";
import { GetFacilityStatus } from "api/rpc/2024-04/facilityAdmin/facility/status";
import { GetBookingCategories } from "api/rpc/2024-04/facilityAdmin/teesheet/bookingCategory";
import axios, { CancelToken } from "axios";
import { formatDate } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { showError } from "redux/actions/ui";
import { TFacilityStatus } from "redux/reducers/models/facility";
import { IBookingCategory, ITeeTimeNew } from "redux/reducers/models/teetime";
import { useDashboardContext } from "./DashboardContext";

interface IProps {
  date: Date;
}

export default function useDashboard(props: IProps) {
  const { date } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [statuses, setStatuses] = useState<Array<TFacilityStatus>>(undefined);
  const [bookingsByCategory, setBookingsByCategory] = useState(undefined);
  const [events, setEvents] = useState(undefined);
  const [teetimes, setTeeTimes] = useState<Array<ITeeTimeNew>>(undefined);
  const { selectedDivisionIds } = useDashboardContext();
  const [bookingCategories, setBookingCategories] = useState<Array<IBookingCategory>>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void getBookingCategories(source.token);
    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (date) {
      void getFacilityStatuses(source.token);
      void getBookingsByCategory(source.token);
      void getEvents(source.token);
    }
    return () => source.cancel();
  }, [date]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (selectedDivisionIds?.length > 0) {
      void loadTeeTimes(source.token);
    }
    return () => source.cancel();
  }, [date, selectedDivisionIds]);

  async function getFacilityStatuses(cancelToken: CancelToken) {
    //show spinner
    if (statuses !== undefined) {
      setStatuses(undefined);
    }
    const res = await GetFacilityStatus({ date: formatDate(date) }, false, cancelToken);
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setStatuses([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting facility statuses")));
      return;
    }
    setStatuses(res?.data);
  }

  async function getBookingsByCategory(cancelToken: CancelToken) {
    //show spinner
    if (bookingsByCategory !== undefined) {
      setBookingsByCategory(undefined);
    }
    const res = await GetBookingsByCategory({ date: formatDate(date) }, false, cancelToken);
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setBookingsByCategory([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting bookings by type")));
      return;
    }
    setBookingsByCategory(res?.data);
  }

  async function getEvents(cancelToken: CancelToken) {
    //show spinner
    if (events !== undefined) {
      setEvents(undefined);
    }
    const res = await GetEvents({ date: formatDate(date) }, false, cancelToken);
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setEvents([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting events")));
      return;
    }
    setEvents(res?.data);
  }

  async function loadTeeTimes(cancelToken: CancelToken) {
    //show spinner
    if (teetimes !== undefined) {
      setTeeTimes(undefined);
    }
    const res = await GetTeeTimesPlayerVolume(
      {
        date: formatDate(date),
        division_ids: selectedDivisionIds,
      },
      false,
      cancelToken,
    );
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }

      setTeeTimes([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting teetimes")));
      return;
    }
    setTeeTimes(res?.data as Array<ITeeTimeNew>);
  }

  async function getBookingCategories(cancelToken: CancelToken) {
    const res = await GetBookingCategories(null, false);
    if (res?.status !== StatusCode.OK) {
      if (cancelToken && cancelToken?.reason) {
        return;
      }
      setBookingCategories([]);
      dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error getting booking categories")));
      return;
    }
    setBookingCategories(res?.data);
  }

  return {
    statuses,
    bookingsByCategory,
    events,
    teetimes,
    bookingCategories,
  };
}
